import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link, useNavigate } from 'react-router-dom';
import useAxios from '@/hooks/use-axios';

import { Download, Image as ImageIcon, Square, Link2, Check } from 'lucide-react';

import CardPackages from '@/components/modules/CardPackages';
import Navbar from '@/components/modules/Navbar';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';


const CORNER_STYLES = [
  { value: 'square', label: 'Square', icon: Square },
  { value: 'rounded', label: 'Rounded', icon: Square },
  // { value: "circle", label: "Circle", icon: Circle },
  // { value: "gapped", label: "Gapped", icon: Grid3X3 },
];


  const STYLE_MODES = [
    { value: 'basic', label: 'Basic', className: 'garuda-shape-basic' },
    { value: 'artistic', label: 'Artistic', className: 'garuda-shape-artistic' },
    { value: 'gapped', label: 'Square', className: 'garuda-shape-square' },
    { value: 'rounded', label: 'Rounded', className: 'garuda-shape-rounded' },
    { value: 'half-rounded', label: 'Half Rounded', className: 'garuda-shape-half-rounded'},
    { value: 'circle', label: 'Circle', className: 'garuda-shape-circle' },
    { value: 'horizontal', label: 'Horizontal', className: 'garuda-shape-horizontal' },
    { value: 'vertical', label: 'Vertical', className: 'garuda-shape-vertical' },
    { value: 'crystal-block', label: 'Crystal Block', className: 'garuda-shape-crystal-block' },
    { value: 'diamond', label: 'Diamond', className: 'garuda-shape-diamond' },
    { value: 'parallelogram', label: 'Parallelog-ram', className: 'garuda-shape-parallelogram' },
    { value: 'sparkle', label: 'Sparkle', className: 'garuda-shape-sparkle' },
    { value: 'circuit', label: 'Circuit', className: 'garuda-shape-circuit' },
    { value: 'cross', label: 'Cross', className: 'garuda-shape-cross' },
    { value: 'cyberpunk', label: 'Cyberpunk', className: 'garuda-shape-cyberpunk' },
    { value: 'full-diamond', label: 'Full Diamond', className: 'garuda-shape-full-diamond' },
    { value: 'full-sparkle', label: 'Full Sparkle', className: 'garuda-shape-full-sparkle' },
    { value: 'flower', label: 'Flower', className: 'garuda-shape-flower' },
  ];

const GRADIENT_DIRECTIONS = [
  { value: 'diagonal', label: 'Diagonal' },
  { value: 'vertical', label: 'Vertical' },
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'radial', label: 'Radial' },
];

  const EYE_FRAME_SHAPES = [
    { value: 'square', label: 'Square', className: 'garuda-border-square'  },
    { value: 'rounded', label: 'Rounded', className: 'garuda-border-rounded'  },
    { value: 'circle', label: 'Circle', className: 'garuda-border-circle'},
    { value: 'leaf', label: 'Leaf', className: 'garuda-border-leaf' },
    { value: 'drop', label: 'Drop', className: 'garuda-border-drop' },
    { value: 'dotted', label: 'Dotted', className: 'garuda-border-dotted'},
    { value: 'rounded-corner', label: 'Rounded Corner', className: 'garuda-border-rounded-corner' },
    { value: 'crystal-block', label: 'Crystal Block', className: 'garuda-border-crystal-block' },
  ];

  const EYE_BALL_SHAPES = [
    { value: 'square', label: 'Square', className: 'garuda-center-square' },
    { value: 'circle', label: 'Circle', className: 'garuda-center-circle' },
    { value: 'rounded', label: 'Rounded', className: 'garuda-center-rounded'  },
    { value: 'rounded-corner', label: 'Rounded Corner', className: 'garuda-center-rounded-corner' },
    { value: 'leaf', label: 'Leaf', className: 'garuda-center-leaf' },
    { value: 'dotted', label: 'Dotted', className: 'garuda-center-dotted' },
    { value: 'crystal-block', label: 'Crystal Block', className: 'garuda-center-crystal-block' },
    { value: 'horizontal', label: 'Horizontal', className: 'garuda-center-horizontal' },
    { value: 'vertical', label: 'Vertical', className: 'garuda-center-vertical' },
    { value: 'merged-dots', label: 'Merged Dots', className: 'garuda-center-merged-dots' },
    { value: 'diamond', label: 'Diamond', className: 'garuda-center-diamond' },
    { value: 'cross', label: 'Cross', className: 'garuda-center-cross' },
  ];

const POSITIONS = [
  { value: 'top-left', label: 'TL' },
  { value: 'top-right', label: 'TR' },
  { value: 'center', label: 'C' },
  { value: 'bottom-left', label: 'BL' },
  { value: 'bottom-right', label: 'BR' },
  { value: 'custom', label: 'XY' },
];

export default function DemoPage() {
  const api = useAxios();

  const navigate = useNavigate();

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [qrContent, setQrContent] = useState('');
  const [qrPreview, setQrPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // QR Config
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [qrSize, setQrSize] = useState(300);
  const [cornerStyle, setCornerStyle] = useState('square');
  const [logoFile, setLogoFile] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [enableTransparentBG, setEnableTranparentBG] = useState(false);

  // Artistic style options
  const [styleMode, setStyleMode] = useState('basic');
  const [accentColor, setAccentColor] = useState('#3B5998');
  const [cornerColor, setCornerColor] = useState('#1E3A8A');
  const [enableCurves, setEnableCurves] = useState(false);

  // Gradient options (available for all modes)
  const [enableGradient, setEnableGradient] = useState(false);
  const [gradientStart, setGradientStart] = useState('#1E3A8A');
  const [gradientEnd, setGradientEnd] = useState('#92400E');
  const [gradientDirection, setGradientDirection] = useState('diagonal');

  // Eye frame shape and eye ball shape
  const [eyeFrameShape, setEyeFrameShape] = useState('square');
  const [eyeBallShape, setEyeBallShape] = useState('square');
  const [eyeColor, setEyeColor] = useState('#000000'); // Custom eye/corner color for gradient mode

  // Background image
  const [bgImageFile, setBgImageFile] = useState(null);
  const [bgImageBase64, setBgImageBase64] = useState(null);

  // Packages for How It Works section
  const [packages, setPackages] = useState([]);

  // Position Config
  const [positionType, setPositionType] = useState('bottom-right');
  const [customX, setCustomX] = useState(50);
  const [customY, setCustomY] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);

  const [subscriptionType, setSubscriptionType] = useState('monthly');
  const [enableTracking, setEnableTracking] = useState(false);
  const [dialogDownload, setDialogDownload] = useState(false);
  const [dialogDownloadWithTracking, setDialogDownloadWithTracking] = useState(false);

  const [dialogAds, setDialogAds] = useState(false);

  const [gradientRadius, setGradientRadius] = useState(0); // Custom border radius if shape rounded
  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);



  // Generate QR Preview
  const generateQRPreview = useCallback(async () => {
    if (!qrContent) return;
    try {
      setIsLoadingGenerate(true);
      const response = await api.post(`/demo/generate-qr-preview`, {
        content: qrContent,
        fg_color: fgColor,
        bg_color: bgColor,
        size: qrSize,
        corner_style: cornerStyle,
        logo_base64: logoBase64,
        style_mode: styleMode,
        accent_color: accentColor,
        corner_color: cornerColor,
        enable_curves: enableCurves,
        bg_image_base64: bgImageBase64,
        enable_gradient: enableGradient,
        gradient_start: gradientStart,
        gradient_end: gradientEnd,
        gradient_direction: gradientDirection,
        eye_frame_shape: eyeFrameShape,
        eye_ball_shape: eyeBallShape,
        eye_color: eyeColor, // Only send eye_color when gradient is enabled
        gradient_percentage: gradientRadius,
        transparent_background: enableTransparentBG
      });
      setQrPreview(response.data.qr_base64);
    } catch (error) {
      console.error('Error generating QR preview:', error);
    } finally {
      setTimeout(() => setIsLoadingGenerate(false), 400);
    }
  }, [
    qrContent,
    fgColor,
    bgColor,
    qrSize,
    cornerStyle,
    logoBase64,
    styleMode,
    accentColor,
    cornerColor,
    enableCurves,
    bgImageBase64,
    enableGradient,
    gradientStart,
    gradientEnd,
    gradientDirection,
    eyeFrameShape,
    eyeBallShape,
    eyeColor,
    gradientRadius,
    enableTransparentBG
  ]);

  // Fetch packages for How It Works section
  const [currency, setCurrency] = useState('USD');

  const moreThan3MinutesAgo = () => {
    const last = localStorage.getItem('lastShowAds');
    if(!last) return true;
    return (Date.now() - parseInt(last)) > 300000
  }

  const fetchPackages = useCallback(async () => {
    try {
      const response = await api.get(`/packages/localized`);
      setCurrency(response.data.currency);
      setPackages(response.data.packages); // Show max 3 packages
      console.log(moreThan3MinutesAgo())
      if(response.data.currency === 'IDR' && moreThan3MinutesAgo()) {
        setDialogAds(true)
        localStorage.setItem('lastShowAds', Date.now());
      };
    } catch (error) {
      console.error('Error fetching packages:', error);
      // Fallback to regular packages
      try {
        const fallbackResponse = await api.get(`/packages`);
        setPackages(fallbackResponse.data);
      } catch (fallbackError) {
        console.error('Error fetching fallback packages:', fallbackError);
      }
    }
  });

  useEffect(() => {
    fetchPackages();
  }, [2000]);

  // Handle background image upload
  const handleBgImageUpload = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setBgImageBase64(base64);
      setBgImageFile(file);
    };
    reader.readAsDataURL(file);
  };

  // Debounced QR preview generation
  useEffect(() => {
    const timer = setTimeout(() => {
      generateQRPreview();
    }, 300);
    return () => clearTimeout(timer);
  }, [generateQRPreview]);

  // Handle PDF upload
  const handlePdfUpload = async (file) => {
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please upload a valid PDF file');
      return;
    }

    setPdfFile(file);

    const formData = new FormData();
    formData.append('pdf_file', file);

    try {
      const response = await api.post(`/demo/pdf-info`, formData);
      setPdfInfo(response.data);
      toast.success(`PDF loaded: ${response.data.total_pages} page(s)`);
    } catch (error) {
      toast.error('Error loading PDF');
      console.error(error);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setLogoBase64(base64);
      setLogoFile(file);
    };
    reader.readAsDataURL(file);
  };

  // Download PDF with QR
  const handleDownload = async () => {
    if (!qrContent) {
      toast.error('Please upload a PDF and enter QR content');
      return;
    }

    if (enableTracking) {
      setDialogDownloadWithTracking(true);
    } else {
      setDialogDownload(true);
    }

    return;
  };

  const downloadQRImage = () => {
    if (!qrContent) {
      toast.error('Please upload a PDF and enter QR content');
      return;
    }
    const url = qrPreview;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `preview_qr.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    setDialogDownload(false);
    return;
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handlePdfUpload(file);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />

      {/* Hero Section */}
      <section className="my-8">
        <div className="container mx-auto px-4 text-center">
          <img className="w-[366px] mb-4 mx-auto" src="/assets/Illustration.svg" />
          <h1 className="text-4xl sm:text-2xl lg:text-[2rem] font-bold text-white leading-[2rem] mb-3">
            Garuda QR
          </h1>
          <p className="text-base md:text-[1.25rem] md:leading-[1.89rem] text-white max-w-2xl mx-auto mb-8">
            Kreasikan QR Code yang stand out. Mudah, cepat, dan eksklusif.
          </p>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo-section" className="">
        <div className="container px-4 xl:px-0 mx-auto max-w-[1140px]">
          <div className="flex flex-col md:flex-row gap-6 w-full rounded-lg card">
            {/* Left Panel - Controls */}
            <div className="space-y-6 w-full lg:min-w-[696px]">
              {/* QR Content */}
              <div className="">
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-orange-500 border-primary-500" />
                  Complete the content
                </h3>
                <Label className="text-sm text-[#A1A1AA]">Link</Label>
                <Input
                  value={qrContent}
                  onChange={(e) => setQrContent(e.target.value)}
                  placeholder="https://"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="qr-content-input"
                />
              </div>

              {/* QR Settings */}
              <div className="">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <i className="garuda-qrcode text-primary-500" />
                  Design QR Code
                </h3>

                <Tabs defaultValue="style" className="w-full">
                  <TabsList className="w-full bg-zinc-950 mb-4">
                    <TabsTrigger value="style" className="text-xs">
                      Shape & Color
                    </TabsTrigger>
                    <TabsTrigger value="logo" className="text-xs">
                      Logo
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="style" className="space-y-4">
                    {/* Style Mode Toggle */}
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Shape Style</Label>
                      <div className="flex flex-wrap gap-[10px] mt-2">
                        {STYLE_MODES.map((mode) => (
                          <button
                            key={mode.value}
                            onClick={() => setStyleMode(mode.value)}
                            className={`w-16 h-[78px] p-2 !pt-3 rounded-lg border text-xs transition-all flex flex-col gap-[10px] items-center ${
                              styleMode === mode.value
                                ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                : 'bg-zinc-950 border-[#697D95] text-[#A1A1AA] hover:border-zinc-700'
                            }`}
                            data-testid={`style-mode-${mode.value}`}
                          >
                            <i className={`text-white ${mode.className} mt-0`} />
                            <span className='text-[10px] leading-3 text-inherit'>
                              {mode.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">
                          {styleMode === 'artistic' ? 'Primary Color' : 'Shape Color'}
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={fgColor}
                            onChange={(e) => setFgColor(e.target.value)}
                            className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                            data-testid="fg-color-input"
                            disabled={enableGradient}
                          />
                          <Input
                            value={fgColor}
                            onChange={(e) => setFgColor(e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-xs font-mono" disabled={enableGradient}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Background</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={bgColor}
                            onChange={(e) => setBgColor(e.target.value)}
                            className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                            data-testid="bg-color-input"
                          />
                          <Input
                            value={bgColor}
                            onChange={(e) => setBgColor(e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 flex-col lg:flex-row">
                      <div className="flex items-center justify-between p-3 rounded-md bg-zinc-950 border border-zinc-800 w-full">
                        <Label className="text-xs text-[#A1A1AA]">Transparent Background</Label>
                        <button
                          onClick={() => setEnableTranparentBG(!enableTransparentBG)}
                          className={`w-10 h-6 rounded-full transition-all ${
                            enableTransparentBG ? 'bg-primary-500' : 'bg-zinc-700'
                          }`}
                          data-testid="enable-gradient-toggle"
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white transition-transform ${
                              enableTransparentBG ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-md bg-zinc-950 border border-zinc-800 w-full">
                        <Label className="text-xs text-[#A1A1AA]">Enable Gradient</Label>
                        <button
                          onClick={() => setEnableGradient(!enableGradient)}
                          className={`w-10 h-6 rounded-full transition-all ${
                            enableGradient ? 'bg-primary-500' : 'bg-zinc-700'
                          }`}
                          data-testid="enable-gradient-toggle"
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white transition-transform ${
                              enableGradient ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                    {/* Gradient Toggle - Available for all modes */}
                  
                    <hr className="border-[#34383b]" />

                    {/* Gradient Options - Show when enabled */}
                    {enableGradient && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-[#A1A1AA]">Gradient Start</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={gradientStart}
                                onChange={(e) => setGradientStart(e.target.value)}
                                className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                                data-testid="gradient-start-input"
                              />
                              <Input
                                value={gradientStart}
                                onChange={(e) => setGradientStart(e.target.value)}
                                className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-[#A1A1AA]">Gradient End</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={gradientEnd}
                                onChange={(e) => setGradientEnd(e.target.value)}
                                className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                                data-testid="gradient-end-input"
                              />
                              <Input
                                value={gradientEnd}
                                onChange={(e) => setGradientEnd(e.target.value)}
                                className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">Gradient Direction</Label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                            {GRADIENT_DIRECTIONS.map((dir) => (
                              <button
                                key={dir.value}
                                onClick={() => setGradientDirection(dir.value)}
                                className={`p-2 rounded-md border text-xs transition-all ${
                                  gradientDirection === dir.value
                                    ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                    : 'bg-zinc-950 border-zinc-800 text-[#A1A1AA] hover:border-zinc-700'
                                }`}
                                data-testid={`gradient-dir-${dir.value}`}
                              >
                                {dir.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {  enableGradient && gradientDirection === 'radial' && <div>
                          <Label className="text-xs text-[#A1A1AA]">Gradient Radius</Label>
                            <div className="flex gap-2 mt-2">
                              <Slider defaultValue={[gradientRadius]} onValueChange={(e) => setGradientRadius(e[0])} />
                            </div>
                        </div> }

                      </>
                    )}

                    {/* Artistic Mode Extra Colors */}
                    {styleMode === 'artistic' && !enableGradient && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">Accent Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={accentColor}
                              onChange={(e) => setAccentColor(e.target.value)}
                              className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                              data-testid="accent-color-input"
                            />
                            <Input
                              value={accentColor}
                              onChange={(e) => setAccentColor(e.target.value)}
                              className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">Corner Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={cornerColor}
                              onChange={(e) => setCornerColor(e.target.value)}
                              className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                              data-testid="corner-color-input"
                            />
                            <Input
                              value={cornerColor}
                              onChange={(e) => setCornerColor(e.target.value)}
                              className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Artistic Curves Toggle */}
                    {styleMode === 'artistic' && false && (
                      <div className="flex items-center justify-between p-3 rounded-md bg-zinc-950 border border-zinc-800">
                        <Label className="text-xs text-[#A1A1AA]">Enable Curves Overlay</Label>
                        <button
                          onClick={() => setEnableCurves(!enableCurves)}
                          className={`w-10 h-6 rounded-full transition-all ${
                            enableCurves ? 'bg-primary-500' : 'bg-zinc-700'
                          }`}
                          data-testid="enable-curves-toggle"
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white transition-transform ${
                              enableCurves ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    )}

                    {/* Corner Style - Only show for basic mode */}
                    {/* {styleMode === "basic" && (
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">
                          Corner Style
                        </Label>
                        <div className="grid grid-cols-4 gap-2 mt-2">
                          {CORNER_STYLES.map((style) => (
                            <button
                              key={style.value}
                              onClick={() => setCornerStyle(style.value)}
                              className={`p-2 rounded-md border text-xs transition-all ${
                                cornerStyle === style.value
                                  ? "bg-primary-500/20 border-primary-500 text-primary-400"
                                  : "bg-zinc-950 border-zinc-800 text-[#A1A1AA] hover:border-zinc-700"
                              }`}
                              data-testid={`corner-style-${style.value}`}
                            >
                              <style.icon className="w-4 h-4 mx-auto mb-1" />
                              {style.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )} */}

                    {/* Eye Frame Shape */}
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Eye Frame Shape</Label>
                      <div className="flex flex-wrap gap-[10px] mt-2">
                        {EYE_FRAME_SHAPES.map((shape) => (
                          <button
                            key={shape.value}
                            onClick={() => setEyeFrameShape(shape.value)}
                            className={`w-16 h-[78px] p-2 !pt-3 rounded-lg border text-xs transition-all flex flex-col gap-[10px] items-center ${
                              eyeFrameShape === shape.value
                                ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                : 'bg-zinc-950 border-[#697D95] text-[#A1A1AA] hover:border-zinc-700'
                            }`}
                            data-testid={`eye-frame-${shape.value}`}
                          >
                            <i className={`text-white ${shape.className}`} />
                            <span className='text-[10px] leading-3 text-inherit'>
                              {shape.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Border Color</Label>
                      <p className="text-xs text-zinc-500 mb-1">Custom color for finder patterns</p>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={eyeColor}
                          onChange={(e) => setEyeColor(e.target.value)}
                          className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                          data-testid="eye-color-input"
                        />
                        <Input
                          value={eyeColor}
                          onChange={(e) => setEyeColor(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                        />
                      </div>
                    </div>

                    <hr className="border-[#34383b]" />

                    {/* Eye Ball Shape */}
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Eye Ball Shape</Label>
                      <p className="text-xs text-zinc-500 mb-1">Inner element of finder patterns</p>
                      <div className="flex flex-wrap gap-[10px] mt-2">
                        {EYE_BALL_SHAPES.map((shape) => (
                          <button
                            key={shape.value}
                            onClick={() => setEyeBallShape(shape.value)}
                            className={`w-16 h-[78px] p-2 !pt-3 rounded-lg border text-xs transition-all flex flex-col gap-[10px] items-center ${
                              eyeBallShape === shape.value
                                ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                : 'bg-zinc-950 border-[#697D95] text-[#A1A1AA] hover:border-zinc-700'
                            }`}
                            data-testid={`eye-ball-${shape.value}`}
                          >
                            <i className={`text-white ${shape.className}`} />
                            <span className='text-[10px] leading-3 text-inherit'>
                              {shape.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="logo" className="space-y-4">
                    {/* Center Logo */}
                    <div>
                      <Label className="text-xs text-[#A1A1AA] mb-2 block">
                        Upload Center Logo
                      </Label>
                      <div
                        className="drop-zone"
                        onClick={() => document.getElementById('logo-input').click()}
                        data-testid="logo-upload-zone"
                      >
                        {logoFile ? (
                          <div className="flex items-center gap-2">
                            <ImageIcon className="w-6 h-6 text-primary-500" />
                            <span className="text-sm text-zinc-300">{logoFile.name}</span>
                          </div>
                        ) : (
                          <>
                            <ImageIcon className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                            <p className="text-sm text-[#A1A1AA]">Add logo to QR center</p>
                          </>
                        )}
                      </div>
                      <input
                        id="logo-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e.target.files[0])}
                      />
                      {logoFile && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setLogoFile(null);
                            setLogoBase64(null);
                          }}
                          className="w-full border-zinc-700 text-[#A1A1AA] mt-2"
                          data-testid="remove-logo-btn"
                        >
                          Remove Logo
                        </Button>
                      )}
                    </div>

                    {/* Background Image */}
                    {/* <div>
                      <Label className="text-xs text-[#A1A1AA] mb-2 block">Background Image</Label>
                      <div
                        className="drop-zone"
                        onClick={() => document.getElementById("bg-image-input").click()}
                        data-testid="bg-image-upload-zone"
                      >
                        {bgImageFile ? (
                          <div className="flex items-center gap-2">
                            <ImageIcon className="w-6 h-6 text-green-500" />
                            <span className="text-sm text-zinc-300">{bgImageFile.name}</span>
                          </div>
                        ) : (
                          <>
                            <Palette className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                            <p className="text-sm text-[#A1A1AA]">Add background image</p>
                          </>
                        )}
                      </div>
                      <input
                        id="bg-image-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleBgImageUpload(e.target.files[0])}
                      />
                      {bgImageFile && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setBgImageFile(null);
                            setBgImageBase64(null);
                          }}
                          className="w-full border-zinc-700 text-[#A1A1AA] mt-2"
                          data-testid="remove-bg-image-btn"
                        >
                          Remove Background
                        </Button>
                      )}
                    </div> */}
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            {/* Right Panel - Preview */}
            <div className="px-0 lg:pl-6 w-full lg:max-w-[380px] h-fit flex flex-col sticky top-[90px]">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <i className="garuda-download text-primary-500 max-h-[20px]" />
                Download QR Code
              </h3>
              <div className="flex-1 flex items-center justify-center">
                {qrContent && isLoadingGenerate ? (
                  <div className="bg-white p-4 rounded-lg shadow-2xl animate-fade-in">
                    <img
                      src="/assets/loading-qr.gif"
                      alt="Preview QR Code"
                      className="max-w-full"
                      style={{ width: qrSize, height: qrSize }}
                      data-testid="qr-preview-image"
                    />
                  </div>
                ) : qrPreview && !isLoadingGenerate ? (
                  <div className="bg-white p-4 rounded-lg shadow-2xl animate-fade-in">
                    <img
                      src={qrPreview}
                      alt="Preview QR Code"
                      className="max-w-full"
                      style={{ width: qrSize, height: qrSize }}
                      data-testid="qr-preview-image"
                    />
                  </div>
                ) : (
                  <div className="flex min-h-[280px] animate-fade-in">
                    <div className="text-center text-zinc-500">
                      <img
                        src="/assets/placeholder-qrcode.png"
                        alt="QR Placeholder"
                        className="mx-auto max-w-full"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 mt-8">
                {/* Download Button */}
                <Button
                  onClick={handleDownload}
                  disabled={!qrContent || isLoading}
                  className="w-full"
                  size="xl"
                  data-testid="download-btn"
                >
                  {isLoading ? (
                    <div className="spinner mr-2" />
                  ) : (
                    <Download className="w-5 h-5 mr-2" />
                  )}
                  Download
                </Button>
              </div>

              <div className="flex item-center gap-3">
                <Switch
                  checked={enableTracking}
                  onCheckedChange={(checked) => setEnableTracking(checked)}
                ></Switch>
                <span>Aktifkan Tracking QR</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="my-14">
        <div className="container mx-auto px-4 max-w-screen-xl">
          <h2 className=" text-white text-center section-title">Kenapa Garuda QR?</h2>
          <p className="section-subtitle text-center mb-9">
            Buat, kreasikan, dan pantau performa QR Code Anda dalam satu tempat. No coding required.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-screen-xl mx-auto">
            <div className="card flex gap-6 items-center !py-[25px]">
              <img src="/assets/WU_1.png" className="w-[100px] h-[100px]" />
              <h3 className="font-bold text-lg text-white mb-2">Tanpa Skill Koding</h3>
            </div>
            <div className="card flex gap-6 items-center !py-[25px]">
              <img src="/assets/WU_2.png" className="w-[100px] h-[100px]" />
              <h3 className="font-bold text-lg text-white mb-2">
                Unlimited Tracking Statistik QR Code
              </h3>
            </div>
            <div className="card flex gap-6 items-center !py-[25px]">
              <img src="/assets/WU_2.png" className="w-[100px] h-[100px]" />
              <h3 className="font-bold text-lg text-white mb-2">
                Atur Warna & Bentuk QR Code Sesuai Selera
              </h3>
            </div>
          </div>
        </div>
      </section>

      <CardPackages
        id="packages"
        packages={packages}
        currency={currency}
        subscriptionType={subscriptionType}
        setSubscriptionType={setSubscriptionType}
      />

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8">
        <div className="container mx-auto px-4 text-center text-sm text-zinc-500">
          <p>Daya Gagas Internasional - Generate QR Codes Garuda QR</p>
        </div>
      </footer>

      <Dialog open={dialogDownload} onOpenChange={setDialogDownload}>
        <DialogContent className="max-w-[572px] p-0 gap-0">
          <DialogHeader className="p-0">
            <img src="/assets/modal-promo.png" className="w-full h-[218px] rounded-t-lg" />
          </DialogHeader>
          <DialogTitle className="hidden">Hidden</DialogTitle>
          <DialogDescription asChild className="p-6 text-center">
            <div className="flex flex-col gap-6 justify-center items-center">
              <img src="/assets/logo_garudaqr.png" className="w-[151px]" />
              <div className="text-center max-w-[390px]">
                <p className="font-bold">
                  Tunggu sebentar! Maksimalkan QR Code Anda sebelum diunduh.
                </p>
                <p className="text-base">Dapatkan akses penuh ke fitur berikut:</p>
              </div>
              <ul className="space-y-4 w-full px-12">
                <li className="text-base font-normal flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary-500 grid place-content-center">
                    <Check className="w-4 h-4 text-white " />
                  </span>
                  <span>Tambahkan custom logo & warna brand.</span>
                </li>
                <li className="text-base font-normal flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary-500 grid place-content-center">
                    <Check className="w-4 h-4 text-white " />
                  </span>
                  <span>Lacak data statistik & performa scan.</span>
                </li>
                <li className="text-base font-normal flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary-500 grid place-content-center">
                    <Check className="w-4 h-4 text-white " />
                  </span>
                  <span>Edit QR Code secara real-time kapan saja.</span>
                </li>
              </ul>
              <div className="flex gap-3 w-full">
                <Button className="w-full" variant="primary" onClick={() => downloadQRImage()}>
                  Download QR
                </Button>
                <Link to="/register" className="w-full">
                  <Button
                    className="w-full"
                    variant="default"
                    onClick={() => setDialogDownloadWithTracking(false)}
                  >
                    Daftar Gratis
                  </Button>
                </Link>
              </div>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
      <Dialog open={dialogDownloadWithTracking} onOpenChange={setDialogDownloadWithTracking}>
        <DialogContent className="max-w-[572px] p-0 gap-0">
          <DialogHeader className="p-0">
            <img src="/assets/modal-tracking.png" className="w-full h-[218px] rounded-t-lg" />
          </DialogHeader>
          <DialogTitle className="hidden">Hidden</DialogTitle>
          <DialogDescription asChild className="p-6 text-center">
            <div className="flex flex-col gap-6 justify-center items-center">
              <img src="/assets/logo_garudaqr.png" className="w-[151px]" />
              <div className="text-center max-w-[390px]">
                <p className="font-bold">Pantau Performa QR Code Anda</p>
                <p className="text-base">
                  Bandingkan jumlah scan dan temukan dari mana audiens terbaik Anda berasal.
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <Button
                  className="w-full"
                  variant="primary"
                  onClick={() => navigate('/sample-tracking')}
                >
                  Lihat Demo
                </Button>
                <Link to="/register" className="w-full">
                  <Button
                    className="w-full"
                    variant="default"
                    onClick={() => setDialogDownloadWithTracking(false)}
                  >
                    Daftar Gratis
                  </Button>
                </Link>
              </div>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
      <Dialog open={dialogAds} onOpenChange={setDialogAds}>
        <DialogContent className="max-w-[594px] p-0 gap-0 bg-white !rounded-[15px]" buttonCloseClassName="text-slate-700/70">
          <DialogHeader className="px-4 pt-[3.375rem]">
            <img src="/assets/inaproc-logo.svg" className="w-full h-[80px] mx-auto" />
          </DialogHeader>
          <DialogTitle className="hidden">Hidden</DialogTitle>
          <DialogDescription asChild className="px-5 pt-5 pb-[46px] text-center !text-slate-900">
            <div className="flex flex-col gap-5 justify-center items-center">
              <div className="flex items-center gap-2 !text-slate-900 ">
                <img src="/assets/garudaqr-black.svg" className="w-[204px]" />
                <span className="!text-slate-900 pt-[4px] font-bold text-2xl">Hadir di</span>
                <span className="!text-slate-900 pt-[4px] font-bold text-2xl">INAPROC</span>
              </div>
              <div className="text-center max-w-[390px] pb-1">
                <p className="text-sm text-[#989898]">INAPROC (Indonesia Procurement) adalah portal nasional pengadaan barang/jasa pemerintah yang dikelola oleh LKPP, berfungsi sebagai pintu gerbang sistem informasi elektronik terintegrasi.</p>
              </div>
              <div className="flex gap-3 w-full items-center justify-center">
                <Link target="_blank" to="https://katalog.inaproc.id/daya-gagas-internasional/garuda-qr-custom-qr-code-generator-unlimited-apihit" className="w-fit">
                  <Button
                    size="xxl"
                    className="w-full bg-gradient-to-r from-[#DA373E] to-[#FA7415] rounded-[10px]"
                    variant="default"
                    onClick={() => setDialogDownloadWithTracking(false)}
                  >
                    Lihat di INAPROC
                  </Button>
                </Link>
              </div>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </div>
  );
}
